import React, { useState, useCallback } from 'react';
import {
  FiBell as Bell,
  FiUser as User,
  FiLink as Link,
  FiZap as Zap,
  FiShield as Shield,
  FiMinimize2 as Minimal,
} from 'react-icons/fi';
import CollapsibleSection from '../components/CollapsibleSection';
import NotificationCatalog from '../components/NotificationCatalog';
import MyAlertsPreferencesSection from '../components/MyAlertsPreferencesSection';
import IntegrationsSection from '../components/IntegrationsSection';
import { notificationCatalogAPI } from '../../../services/realApi';
import { useClient } from '../../../contexts/ClientContext';

/**
 * Unified Notifications Settings Page
 *
 * Combines all notification-related settings into a single page with 3 collapsible sections:
 * 1. System Notifications - Org-wide notification type configuration (Admin)
 * 2. My Alerts & Preferences - Personal preferences + custom alert rules (combined)
 * 3. Integrations - Channel setup (Slack, Email, Webhook)
 */
const NotificationsSettingsPage: React.FC = () => {
  const { selectedClientId } = useClient();
  const [applyingPreset, setApplyingPreset] = useState<string | null>(null);
  const [presetSuccess, setPresetSuccess] = useState<string | null>(null);

  const clientId = selectedClientId;

  // Quick action presets
  const applyPreset = useCallback(async (preset: string) => {
    setApplyingPreset(preset);
    setPresetSuccess(null);

    try {
      // Apply preset to notification catalog
      const catalogResult = await notificationCatalogAPI.getCatalog(null, clientId);
      if (catalogResult.success && catalogResult.items) {
        for (const item of catalogResult.items) {
          let shouldEnable = false;

          switch (preset) {
            case 'all':
              shouldEnable = true;
              break;
            case 'critical':
              shouldEnable = ['critical', 'warning'].includes(item.default_severity);
              break;
            case 'minimal':
              shouldEnable = item.default_severity === 'critical';
              break;
            default:
              shouldEnable = item.is_enabled;
          }

          if (item.is_enabled !== shouldEnable) {
            await notificationCatalogAPI.updateClientConfig(item.code, { is_enabled: shouldEnable }, clientId);
          }
        }
      }

      setPresetSuccess(preset);
      setTimeout(() => setPresetSuccess(null), 3000);

      // Trigger refresh by reloading page sections
      globalThis.dispatchEvent(new CustomEvent('notifications-preset-applied'));
    } catch (err: unknown) {
      console.error('Failed to apply preset:', err);
    } finally {
      setApplyingPreset(null);
    }
  }, [clientId]);

  const presets = [
    { id: 'all', label: 'All On', icon: Zap, description: 'Enable all notifications' },
    { id: 'critical', label: 'Critical Only', icon: Shield, description: 'Only critical and warning notifications' },
    { id: 'minimal', label: 'Minimal', icon: Minimal, description: 'Only critical notifications' },
  ];

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
          Notifications
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
          Configure how your organization and you receive notifications
        </p>
      </div>

      {/* Quick Actions Bar */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300">Quick Actions</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Apply preset configurations</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {presets.map((preset) => {
              const Icon = preset.icon;
              const isApplying = applyingPreset === preset.id;
              const isSuccess = presetSuccess === preset.id;
              let presetButtonLabel: string;
              if (isApplying) {
                presetButtonLabel = 'Applying...';
              } else if (isSuccess) {
                presetButtonLabel = 'Applied!';
              } else {
                presetButtonLabel = preset.label;
              }

              return (
                <button
                  key={preset.id}
                  onClick={() => applyPreset(preset.id)}
                  disabled={applyingPreset !== null}
                  title={preset.description}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                    isSuccess
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <Icon className={`w-4 h-4 ${isApplying ? 'animate-pulse' : ''}`} />
                  {presetButtonLabel}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {/* Section 1: System Notifications (Admin) - Collapsed by default */}
        <CollapsibleSection
          title="System Notifications"
          description="Configure which notifications your organization receives"
          icon={Bell}
          badge="Admin"
          count="Notification types"
          defaultExpanded={false}
        >
          <NotificationCatalog compact />
        </CollapsibleSection>

        {/* Section 2: My Alerts & Preferences (Personal) - Expanded by default */}
        <CollapsibleSection
          title="My Alerts & Preferences"
          description="Configure your notification preferences and custom alert rules"
          icon={User}
          count="Preferences + Rules"
          defaultExpanded={true}
        >
          <MyAlertsPreferencesSection clientId={clientId ?? undefined} {...{ compact: true } as any} />
        </CollapsibleSection>

        {/* Section 3: Integrations (Channel Setup) - Collapsed by default */}
        <CollapsibleSection
          title="Integrations"
          description="Connect notification channels like Slack and webhooks"
          icon={Link}
          count="3 channels"
          defaultExpanded={false}
        >
          <IntegrationsSection />
        </CollapsibleSection>
      </div>
    </div>
  );
};

export default NotificationsSettingsPage;
